import type { ApiEnvelope, ApiError as ApiErrorBody } from '@tunodo/ticketing-api-types';
import type { ZodType } from 'zod';
import { useAuthStore } from '@/stores/auth-store';

/** Cuerpo de error en ambos contratos: envelope de Go (`error`) y Node (`message`). */
type ErrorBody = { error?: Partial<ApiErrorBody> | null; message?: unknown };

/** Cuerpo de exito del envelope de Go, con `data` y `meta` sin validar. */
type Envelope = Partial<ApiEnvelope<unknown>> & { success: boolean };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isEnvelope(value: unknown): value is Envelope {
  return isRecord(value) && typeof value.success === 'boolean';
}

/** Detalle de una regla de validacion incumplida (`error.details` de Go). */
export type ApiErrorDetail = { field: string; rule: string; param?: string };

/**
 * Mensaje de un cuerpo de error. La API de Go responde con el envelope
 * `{success, data, error: {code, message}, meta}` y el contrato viejo (Node)
 * con `{message}`. Se prefiere `error.message` y se conserva `message`.
 */
export function messageFromBody(body: unknown): string | undefined {
  if (!isRecord(body)) return undefined;
  const { error, message } = body as ErrorBody;
  if (isRecord(error) && typeof error.message === 'string') return error.message;
  if (typeof message === 'string') return message;
  return undefined;
}

function detailsFromBody(body: unknown): ApiErrorDetail[] {
  if (!isRecord(body) || !isRecord(body.error) || !Array.isArray(body.error.details)) return [];
  const details: ApiErrorDetail[] = [];
  for (const item of body.error.details) {
    if (!isRecord(item) || typeof item.field !== 'string' || typeof item.rule !== 'string') {
      continue;
    }
    const param = typeof item.param === 'string' && item.param !== '' ? item.param : undefined;
    details.push(
      param === undefined
        ? { field: item.field, rule: item.rule }
        : { field: item.field, rule: item.rule, param },
    );
  }
  return details;
}

/** Agrega al mensaje el campo y la regla de cada detalle, por ejemplo `name (min 1)`. */
function withDetails(message: string, details: ApiErrorDetail[]): string {
  if (details.length === 0) return message;
  const parts = details.map((d) => `${d.field} (${d.param ? `${d.rule} ${d.param}` : d.rule})`);
  return `${message}: ${parts.join(', ')}`;
}

async function readError(
  response: Response,
): Promise<{ message: string; details: ApiErrorDetail[] }> {
  try {
    const body: unknown = await response.json();
    const details = detailsFromBody(body);
    return { message: withDetails(messageFromBody(body) ?? response.statusText, details), details };
  } catch {
    return { message: response.statusText, details: [] };
  }
}

const LOGIN_PATH = '/api/auth/login';

/** Un 401 del login es "credenciales invalidas", no una sesion vencida. */
function isLoginRequest(input: RequestInfo): boolean {
  const url =
    typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
  return url.split('?')[0]?.endsWith(LOGIN_PATH) ?? false;
}

/** Construye el ApiError de una respuesta no OK, sin tocar la sesion. */
async function toApiError(response: Response): Promise<ApiError> {
  const { message, details } = await readError(response);
  return new ApiError(response.status, message, details);
}

/** Un 401 fuera del login limpia la sesion y manda a /login (replace evita bucles con Atras). */
function handleUnauthorized(response: Response, input: RequestInfo): void {
  if (response.status !== 401 || isLoginRequest(input)) return;
  useAuthStore.getState().clear();
  window.location.replace('/login');
}

/**
 * Desenvuelve el envelope de exito. Cuando `meta` trae paginacion (`total`,
 * `page`, `limit`) y `data` es una lista, reconstruye la forma que consumen
 * las rutas y sus esquemas Zod: `{items, total, page, pageSize}`.
 * Un cuerpo que no es un envelope (contrato viejo) se devuelve tal cual.
 */
function unwrapEnvelope(json: unknown): unknown {
  if (!isEnvelope(json) || !('data' in json)) return json;
  const { data, meta } = json;
  if (Array.isArray(data) && isRecord(meta) && typeof meta.total === 'number') {
    return { items: data, total: meta.total, page: meta.page, pageSize: meta.limit };
  }
  return data;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details: ApiErrorDetail[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Fetch wrapper that:
 * - Injects Bearer token from auth store (no React subscription).
 * - Throws ApiError on non-OK responses.
 * - On 401 (except the login request itself): clears the auth store and redirects to /login.
 * - Optionally validates the parsed JSON with a Zod schema.
 *
 * NOTE: All paths should start with /api, the API uses setGlobalPrefix('api')
 * so both dev (Vite proxy) and prod (nginx) route /api/* to the backend.
 */
export async function apiFetch<T = unknown>(
  input: RequestInfo,
  init?: RequestInit & { schema?: ZodType<T> },
): Promise<T> {
  const { schema, ...fetchInit } = init ?? {};

  const token = useAuthStore.getState().token;
  const headers = new Headers(fetchInit.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (!headers.has('Content-Type') && fetchInit.body) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(input, { ...fetchInit, headers });

  if (!response.ok) {
    handleUnauthorized(response, input);
    throw await toApiError(response);
  }

  // Handle empty responses (e.g., 204 No Content)
  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }

  const json: unknown = unwrapEnvelope(await response.json());

  if (schema) {
    return schema.parse(json);
  }

  return json as T;
}

/**
 * Fetch wrapper for multipart/form-data requests.
 * Does NOT set Content-Type: the browser sets it with the correct multipart boundary.
 * Mirrors apiFetch: injects Bearer, throws ApiError, handles 401.
 */
export async function apiFetchMultipart<T = unknown>(
  input: RequestInfo,
  formData: FormData,
  schema?: ZodType<T>,
): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers = new Headers();

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(input, { method: 'POST', headers, body: formData });

  if (!response.ok) {
    handleUnauthorized(response, input);
    throw await toApiError(response);
  }

  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }

  const json: unknown = unwrapEnvelope(await response.json());

  if (schema) {
    return schema.parse(json);
  }

  return json as T;
}

/**
 * Streaming fetch wrapper for NDJSON endpoints.
 * - Injects Bearer token from auth store.
 * - Throws ApiError on non-OK responses.
 * - Yields each parsed JSON object from the newline-delimited stream.
 */
export async function* streamNdjson<T = unknown>(
  input: RequestInfo,
  init?: RequestInit,
): AsyncGenerator<T> {
  const token = useAuthStore.getState().token;
  const headers = new Headers((init as RequestInit | undefined)?.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('Content-Type') && (init as RequestInit | undefined)?.body) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(input, { ...init, headers });

  if (!response.ok) {
    throw await toApiError(response);
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed) yield JSON.parse(trimmed) as T;
      }
    }
    // procesar buffer residual
    const trimmed = buffer.trim();
    if (trimmed) yield JSON.parse(trimmed) as T;
  } finally {
    reader.releaseLock();
  }
}
